import { useGetInviteRepQrCodeQuery } from 'lib/apollo/hooks'
import { useEffect, useState, useCallback } from 'react'
import { TInviteRepProps, TInviteRepState } from './InviteRep.types'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useGenerateRepToken } from 'lib/services/restApi/generateRepToken'
import { TGenerateRepToken } from 'lib/services/restApi/generateRepToken/types'
import { TRestResponse } from 'lib/services/restApi/restApi.types'
import { Option } from 'common/types'

export const useInviteRepLogic = (props: TInviteRepProps) => {
  const { surgeryId, type } = props
  const [error, setError] = useState<string | null>(null)
  const [navigationUrl, setNavigationUrl] = useState<string>('')
  const [timeRemaining, setTimeRemaining] = useState<number>(0)
  const [codeGenerated, setCodeGenerated] = useState<boolean>(false)
  const [state, setState] = useState<TInviteRepState>({
    selectedCompany: null,
    canAddAsset: false,
    isGuestRep: type === 'guest',
  })

  const {
    loading: regularLoading,
    data,
    error: fetchError,
    refetch,
  } = useGetInviteRepQrCodeQuery(state.canAddAsset, surgeryId)

  const { mutateAsync: generateRepToken, isLoading: guestLoading } =
    useGenerateRepToken()

  const { showCopyLinkButtonForInviteRep } = useFlags()

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(navigationUrl)
    } catch (err) {
      console.error('Failed to copy link: ', err)
      alert('Failed to copy link. Please try again.')
    }
  }

  const toggleRepType = () => {
    setState((prev) => ({
      ...prev,
      isGuestRep: !prev.isGuestRep,
      selectedCompany: null,
    }))
    setNavigationUrl('')
    setError(null)
  }

  useEffect(() => {
    setState((prev) => ({ ...prev, isGuestRep: type === 'guest' }))
  }, [type])

  useEffect(() => {
    setState((prev) => ({ ...prev, isGuestRep: true }))
  }, [])

  // Regular rep logic
  useEffect(() => {
    if (state.isGuestRep) return

    if (regularLoading || !data) return

    if (data.getInviteRepQrCode && !data.getInviteRepQrCode.success) {
      setError(`Error: ${data.getInviteRepQrCode.message}`)
      return
    }

    if (fetchError) {
      setError(`Error: ${fetchError.message}`)
      return
    }

    setNavigationUrl(
      `${window.location.protocol}//${window.location.host}/invite/${data.getInviteRepQrCode.data.jwt}`
    )
  }, [regularLoading, data, fetchError, state.isGuestRep])

  useEffect(() => {
    if (!state.isGuestRep) {
      refetch({ canAddAsset: state.canAddAsset, surgeryId })
    }
  }, [state.canAddAsset, refetch, surgeryId, state.isGuestRep])

  // Guest rep logic
  const handleGenerateGuestToken = useCallback(async () => {
    if (!state.isGuestRep) return

    setError(null)
    setCodeGenerated(false)
    setNavigationUrl('')
    setTimeRemaining(0)

    if (!state.selectedCompany?.id) {
      setError('Please select a company.')
      return
    }

    try {
      const response: TRestResponse<TGenerateRepToken> = await generateRepToken(
        {
          canAddAsset: state.canAddAsset,
          surgeryId,
          companyId: state.selectedCompany?.id as string,
          companyName: state.selectedCompany?.name as string,
        }
      )

      if (response?.success && response?.data?.jwt) {
        setNavigationUrl(
          `${window.location.protocol}//${window.location.host}${response.data.verificationUrl}`
        )
        setCodeGenerated(true)
        if (response.data.expiresAt) {
          const expiryTime = new Date(response.data.expiresAt).getTime()
          const currentTime = new Date().getTime()
          const remainingTimeInSeconds = Math.max(
            0,
            Math.floor((expiryTime - currentTime) / 1000)
          )
          setTimeRemaining(remainingTimeInSeconds)
        }
      } else {
        setError(`Error: ${response?.message || 'Failed to generate token.'}`)
      }
    } catch (err: any) {
      setError(`Error: ${err?.message || 'Failed to generate token.'}`)
    }
  }, [state, surgeryId, generateRepToken])

  const handleCompanySelect = (option: Option) => {
    setState((prev) => ({ ...prev, selectedCompany: option }))
  }

  useEffect(() => {
    if (state.isGuestRep && state.selectedCompany?.id) {
      handleGenerateGuestToken()
    }
  }, [state.selectedCompany, handleGenerateGuestToken, state.isGuestRep])

  const regenerateCode = handleGenerateGuestToken

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`
  }

  useEffect(() => {
    let timerId: NodeJS.Timeout | null = null

    if (codeGenerated && timeRemaining > 0) {
      timerId = setInterval(() => {
        setTimeRemaining((prev) => Math.max(0, prev - 1))
      }, 1000)
    }

    return () => {
      if (timerId) clearInterval(timerId)
    }
  }, [codeGenerated, timeRemaining])

  const handleSwitchChange = () => {
    setState((prev) => ({ ...prev, canAddAsset: !prev.canAddAsset }))
  }

  return {
    error,
    loading: state.isGuestRep ? guestLoading : regularLoading,
    navigationUrl,
    regenerateCode,
    timeRemaining,
    codeGenerated,
    formatTime,
    isExpired: timeRemaining === 0 && state.isGuestRep,
    state,
    setState,
    handleCompanySelect,
    handleCopyLink,
    showCopyLinkButtonForInviteRep,
    handleSwitchChange,
    toggleRepType,
  }
}
